import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldWarning, Flame, ShieldCheck, ArrowRight, CalendarBlank } from "@phosphor-icons/react";

/**
 * ComplianceRadarCard — Dashboard widget showing every property compliance
 * expiry within the next 60 days (rental licenses + fire inspections).
 * Pulled from property_configs.details via GET /api/properties/compliance/upcoming.
 */
export default function ComplianceRadarCard({ days = 60 }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/properties/compliance/upcoming?days=${days}`)
      .then(r => { setRows(r.data.rows || []); setCounts(r.data.counts || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || rows.length === 0) return null;

  return (
    <div className="rugged-card p-4 border-l-4 border-red-500" data-testid="compliance-radar-card">
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldWarning weight="fill" size={18} className="text-red-600"/>
        <h3 className="font-display font-black text-lg uppercase">Compliance Radar</h3>
        <span className="text-[10px] text-slate-500 font-medium">
          · next {days}d
        </span>
        <div className="ml-auto flex flex-wrap gap-1">
          {counts.expired > 0 && (
            <span className="px-2 py-0.5 bg-red-600 text-white font-black uppercase text-[9px] tracking-widest rounded" data-testid="cr-count-expired">
              {counts.expired} expired
            </span>
          )}
          {counts.soon > 0 && (
            <span className="px-2 py-0.5 bg-orange-500 text-white font-black uppercase text-[9px] tracking-widest rounded" data-testid="cr-count-soon">
              {counts.soon} ≤30d
            </span>
          )}
          {counts.ok > 0 && (
            <span className="px-2 py-0.5 bg-slate-200 text-slate-700 font-black uppercase text-[9px] tracking-widest rounded" data-testid="cr-count-ok">
              {counts.ok} upcoming
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto">
        {rows.map((r, i) => <ComplianceRow key={i} r={r}/>)}
      </div>
    </div>
  );
}

function ComplianceRow({ r }) {
  const badge = {
    expired: { bg: "bg-red-600", text: "text-white", label: `${Math.abs(r.days_until)}d overdue` },
    soon:    { bg: "bg-orange-500", text: "text-white", label: `${r.days_until}d left` },
    ok:      { bg: "bg-emerald-500", text: "text-white", label: `${r.days_until}d left` },
  }[r.status];
  const Icon = r.icon === "flame" ? Flame : ShieldCheck;
  return (
    <Link to={`/admin/properties/${encodeURIComponent(r.address)}`}
      data-testid={`cr-row-${r.field}-${r.address_key}`}
      className="flex items-center gap-2 p-2 border-2 border-slate-200 hover:border-slate-500 hover:shadow-sm rounded transition-all group">
      <Icon weight="fill" size={14} className={r.status === "expired" ? "text-red-600" : r.status === "soon" ? "text-orange-500" : "text-emerald-600"}/>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-black text-slate-900 truncate">
          {r.label}
          {r.extra && <span className="ml-1 text-slate-500 font-mono text-[10px]">#{r.extra}</span>}
        </div>
        <div className="text-[11px] text-slate-500 truncate inline-flex items-center gap-1">
          <CalendarBlank size={9}/> {r.expires_on}
          <span className="opacity-50">·</span>
          <span className="truncate">{r.address}</span>
        </div>
      </div>
      <span className={`px-1.5 py-0.5 font-black uppercase text-[9px] tracking-widest rounded shrink-0 ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
      <ArrowRight size={12} className="text-slate-300 group-hover:text-slate-700 shrink-0"/>
    </Link>
  );
}
